import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { ShipmentFormModal } from "@/components/ShipmentFormModal";
import { ReceiptModal } from "@/components/ReceiptModal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, formatApiErrorDetail, ALL_STATUSES } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, Search, Copy, FileText, Pencil, Package } from "lucide-react";

const KPI = [
  { key: "total", label: "Total", color: "text-slate-100" },
  { key: "In Transit", label: "In Transit", color: "text-amber-400" },
  { key: "Out for Delivery", label: "Out for Delivery", color: "text-orange-400" },
  { key: "Delivered", label: "Delivered", color: "text-emerald-400" },
  { key: "Exception/Held", label: "Exceptions", color: "text-rose-400" },
];

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdmin = Boolean(user) && user.role === "admin";
  const [shipments, setShipments] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      const [sRes, statRes] = await Promise.all([
        api.get("/shipments", { params }),
        api.get("/shipments/stats"),
      ]);
      setShipments(sRes.data);
      setStats(statRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load shipments");
    }
  }, [q, status]);

  useEffect(() => {
    // Hard client-side guard: non-admins can never load admin data or see this view.
    if (!authLoading && !isAdmin) {
      navigate(user ? "/my-shipments" : "/login", { replace: true });
    }
  }, [authLoading, isAdmin, user, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, isAdmin]);

  if (!isAdmin) return null;

  const changeStatus = async (shipment, newStatus) => {
    try {
      const { data } = await api.patch(`/shipments/${shipment.shipment_id}/status`, { status: newStatus, note: "" });
      toast.success(`${shipment.tracking_number} → ${newStatus}`);
      setShipments((prev) => prev.map((s) => (s.shipment_id === data.shipment_id ? data : s)));
      api.get("/shipments/stats").then(({ data: st }) => setStats(st));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    }
  };

  const copy = (tn) => {
    navigator.clipboard?.writeText(tn);
    toast.success("Tracking number copied");
  };

  const kpiVal = (k) => (k === "total" ? stats.total : stats.by_status?.[k] || 0);

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight sm:text-3xl">Admin Console</h1>
            <p className="text-sm text-slate-400">Create shipments, manage status and the full audit trail.</p>
          </div>
          <Button
            data-testid={ADMIN.openCreate}
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="bg-orange-600 text-white hover:bg-orange-700 active:scale-[0.98]"
          >
            <Plus className="mr-1 h-4 w-4" /> Create Shipment
          </Button>
        </div>

        {/* KPIs */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {KPI.map((k) => (
            <div key={k.key} className="rounded-xl border border-white/10 bg-slate-900/60 p-4">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{k.label}</p>
              <p className={`mt-1 text-3xl font-black ${k.color}`}>{kpiVal(k.key)}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={ADMIN.search}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tracking no, sender, receiver, city…"
              className="border-white/10 bg-slate-900/60 pl-9"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid={ADMIN.filterStatus} className="w-full border-white/10 bg-slate-900/60 sm:w-56">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-slate-900/40">
          <Table data-testid={ADMIN.table}>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-slate-400">Tracking</TableHead>
                <TableHead className="text-slate-400">Route</TableHead>
                <TableHead className="text-slate-400">Receiver</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Update</TableHead>
                <TableHead className="text-right text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shipments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-slate-500">
                    <Package className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    No shipments match your filters.
                  </TableCell>
                </TableRow>
              )}
              {shipments.map((s) => (
                <TableRow key={s.shipment_id} data-testid={`${ADMIN.row}${s.tracking_number}`} className="border-white/10">
                  <TableCell>
                    <button onClick={() => copy(s.tracking_number)} className="group flex items-center gap-1.5 font-mono text-sm font-bold text-white">
                      {s.tracking_number}
                      <Copy className="h-3.5 w-3.5 text-slate-500 opacity-0 group-hover:opacity-100" />
                    </button>
                    <p className="text-xs text-slate-500">{s.sender_name}</p>
                  </TableCell>
                  <TableCell className="text-sm text-slate-300">{s.origin} → {s.destination}</TableCell>
                  <TableCell className="text-sm text-slate-300">
                    {s.receiver_name}
                    {s.customer_email && <p className="text-xs text-slate-500">{s.customer_email}</p>}
                  </TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell>
                    <Select value={s.status} onValueChange={(v) => changeStatus(s, v)}>
                      <SelectTrigger data-testid={`${ADMIN.updateStatus}${s.tracking_number}`} className="h-8 w-40 border-white/10 bg-slate-800 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALL_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-white" onClick={() => { setEditing(s); setShowForm(true); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button data-testid={`${ADMIN.viewReceipt}${s.tracking_number}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-white" onClick={() => setReceipt(s)}>
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <ShipmentFormModal open={showForm} onOpenChange={setShowForm} onSaved={load} initial={editing} />
      <ReceiptModal open={Boolean(receipt)} onOpenChange={(v) => !v && setReceipt(null)} shipment={receipt} />
    </div>
  );
}
